/**
 * SDZ Pulse — immersive PDP motion.
 *
 * Adds the scroll-driven dimension to the product gallery: each media frame drifts and settles as
 * it crosses the viewport, and reveals once on first entry. Pairs with assets/sdz-pulse-pdp.css,
 * which owns every visual decision; this file only writes two custom properties and two data
 * attributes, so all of the styling stays overridable in CSS.
 *
 * DELIBERATE BOUNDARIES
 *   - The gallery, carousel, zoom and hotspots already exist in the theme (media-gallery.js,
 *     drag-zoom-wrapper.js, zoom-dialog.js, product-hotspot.js). This file never reimplements,
 *     wraps or intercepts any of them.
 *   - It writes to the media CONTAINER, never to .product-media__image. drag-zoom-wrapper.js owns
 *     `transform` on that image; two writers on one property is a bug that only shows up the moment
 *     a customer tries to zoom, so the boundary is enforced here rather than hoped for.
 *   - Outside the product template this does nothing at all, because .product-information is absent.
 *
 * WHY THE FRAME COUNT MATTERS HERE SPECIFICALLY
 * Pulse is a print-on-demand catalogue, and a Printify mockup set for a hoodie runs to 42 images
 * (see Heavy Fleece Sweatpants). Naive parallax on 42 full-bleed frames will melt a phone, so the
 * IntersectionObserver below is not a nicety: only on-screen frames are ever written, and frames
 * that scroll away are marked idle so the compositor releases their layers.
 *
 * ACCESSIBILITY / PERFORMANCE
 *   - Honours prefers-reduced-motion: with it set, no attributes are written and no listener runs.
 *     The page keeps the full static treatment.
 *   - A single rAF loop, only alive while at least one frame is on screen.
 *   - Purely decorative, so a failure here degrades to a clean static product page.
 */
(function () {
  "use strict";

  if (typeof document === "undefined") return;

  var PDP_ROOT = ".product-information";
  var MEDIA_CONTAINER = ".product-information__media .product-media";
  var ROOT_CLASS = "sdz-pulse-pdp";

  /* Drift in px. Small on purpose: a full-bleed garment shot moving more than ~30px starts to feel
     like a parallax demo rather than a product page. */
  var DRIFT_PX = 28;
  /* Settles from 1.04 to 1.00 as the frame leaves centre. Anything above ~1.05 visibly crops. */
  var MAX_SCALE = 1.04;
  /* Below this width the gallery is a single fast-scrolling column and the drift is imperceptible
     while still costing frames, so it is not worth the battery. */
  var MIN_WIDTH_FOR_DRIFT = 640;

  var reduceMotion = false;
  try {
    reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (error) {
    reduceMotion = false;
  }

  /* ------------------------------------------------------------------ *
   * Static treatment                                                    *
   * ------------------------------------------------------------------ */

  /**
   * Tags the gallery frames. Called on init and again after the theme editor re-renders a section,
   * so attribute writing is idempotent.
   */
  function tagFrames(root) {
    var frames = root.querySelectorAll(MEDIA_CONTAINER);
    var tagged = [];

    Array.prototype.forEach.call(frames, function (frame) {
      if (!frame.hasAttribute("data-sdz-pdp-reveal")) {
        frame.setAttribute("data-sdz-pdp-reveal", "");
      }
      tagged.push(frame);
    });

    /* Only the first frame gets the gold wash (see the CSS). Marking it in the DOM keeps the CSS
     * from depending on :first-child, which the carousel reorders when it clones.
     *
     * WHY THIS DOES NOT JUST TAKE tagged[0]
     * Horizon clones slides for looping, so a 36-image Printify set renders as 108 frames: 36 real
     * ones plus 72 zero-size clones. DOM index 0 is a CLONE at 0x0, so tagging tagged[0] painted the
     * gold wash onto an invisible node and the real hero never got it. Pick the first frame that
     * actually has a box instead, and only fall back to tagged[0] if none do (which would mean the
     * gallery has not laid out yet). */
    var hero = null;
    for (var i = 0; i < tagged.length; i += 1) {
      var rect = tagged[i].getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        hero = tagged[i];
        break;
      }
    }

    if (hero) hero.setAttribute("data-sdz-first-frame", "");

    return tagged;
  }

  /* ------------------------------------------------------------------ *
   * Motion                                                              *
   * ------------------------------------------------------------------ */

  function createMotion(frames) {
    var active = [];
    var queued = false;
    var lastValues = new WeakMap();
    var driftEnabled = true;
    var resizeTimer = null;

    function measureDrift() {
      driftEnabled = window.innerWidth >= MIN_WIDTH_FOR_DRIFT;
    }

    /** Per-frame geometry -> the two custom properties the CSS consumes. */
    function applyFrame(frame) {
      var rect = frame.getBoundingClientRect();
      var viewportH = window.innerHeight || 1;

      /* progress runs -1 (frame below the fold) -> 0 (centred) -> +1 (frame above the fold). */
      var center = rect.top + rect.height / 2;
      var span = viewportH / 2 + rect.height / 2;
      var progress = span === 0 ? 0 : (center - viewportH / 2) / span;

      if (progress > 1) progress = 1;
      else if (progress < -1) progress = -1;

      var y = driftEnabled ? -progress * DRIFT_PX : 0;
      var scale = MAX_SCALE - Math.abs(progress) * (MAX_SCALE - 1);

      var previous = lastValues.get(frame);
      /* Skip the write when nothing moved meaningfully. Custom-property writes invalidate style on
         the subtree, so on a still page this turns hundreds of writes per second into zero — which
         is what keeps a 42-frame gallery cheap. */
      if (previous && Math.abs(previous.y - y) < 0.15 && Math.abs(previous.scale - scale) < 0.0008) {
        return;
      }
      lastValues.set(frame, { y: y, scale: scale });

      frame.style.setProperty("--sdz-parallax-y", y.toFixed(2) + "px");
      frame.style.setProperty("--sdz-parallax-scale", scale.toFixed(4));
    }

    function tick() {
      queued = false;
      for (var i = 0; i < active.length; i += 1) applyFrame(active[i]);
    }

    function schedule() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(tick);
    }

    function onResize() {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        measureDrift();
        /* A resize reflows everything, so the cached deltas are no longer trustworthy. */
        lastValues = new WeakMap();
        schedule();
      }, 150);
    }

    /* Reveal + membership in the parallax set are the same question ("is this frame on screen?"),
       so they share one observer rather than two competing ones. */
    var io =
      "IntersectionObserver" in window
        ? new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                var frame = entry.target;

                if (entry.isIntersecting) {
                  frame.setAttribute("data-sdz-inview", "");
                  frame.removeAttribute("data-sdz-idle");
                  if (active.indexOf(frame) === -1) active.push(frame);
                  applyFrame(frame);
                } else {
                  /* Keep the reveal latched so a frame that scrolls back into view does not
                     re-animate. Only its parallax participation is dropped. */
                  frame.setAttribute("data-sdz-idle", "");
                  var index = active.indexOf(frame);
                  if (index !== -1) active.splice(index, 1);
                }
              });
              if (active.length) schedule();
            },
            { rootMargin: "12% 0px 12% 0px", threshold: 0 },
          )
        : null;

    function start() {
      measureDrift();

      if (!io) {
        /* No IntersectionObserver: latch every reveal and set a flat scale. The page must never be
           left with opacity:0 frames, which is the one failure mode that actually hides products. */
        frames.forEach(function (frame) {
          frame.setAttribute("data-sdz-inview", "");
          frame.style.setProperty("--sdz-parallax-scale", "1");
        });
        return;
      }

      frames.forEach(function (frame) {
        /* `data-sdz-parallax` is what the CSS keys the translate/scale off. Without it the JS
         * computed --sdz-parallax-y every frame and the browser threw it away: the custom property
         * was written, nothing consumed it, and the page silently had no motion at all. The
         * attribute is the only thing that switches the CSS rule on. */
        frame.setAttribute("data-sdz-parallax", "");
        io.observe(frame);
      });

      window.addEventListener("scroll", schedule, { passive: true });
      window.addEventListener("resize", onResize, { passive: true });
    }

    return { start: start };
  }

  /* ------------------------------------------------------------------ *
   * Boot                                                                *
   * ------------------------------------------------------------------ */

  function init() {
    var root = document.querySelector(PDP_ROOT);
    if (!root) return;

    /* The CSS keys almost everything off this ancestor, which is how the effect stays scoped to the
       product page without editing templates/product.json. */
    root.classList.add(ROOT_CLASS);

    var frames = tagFrames(root);
    if (!frames.length) return;

    /* Reduced motion: ship the static page. Attributes for the gold wash are already set above,
       which is the one piece of this that is not motion. */
    if (reduceMotion) return;

    createMotion(frames).start();
  }

  function boot() {
    try {
      init();
    } catch (error) {
      /* Decorative only. A throw here must never take the buy button down with it. */
      if (window.console && window.console.warn) {
        window.console.warn("[sdz-pulse-pdp] motion disabled:", error && error.message);
      }
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* The Shopify theme editor swaps section markup without a page load, so re-tag after it does.
     Guarded because the event is editor-only. */
  document.addEventListener("shopify:section:load", function (event) {
    if (!event.target || !event.target.querySelector(PDP_ROOT)) return;
    boot();
  });
})();
